import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardList, Eye, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import ChantierQualificationDetailDialog from './ChantierQualificationDetailDialog';

// Panneau synthèse compact dans le module Travaux d’optimisation (spec §20)
// Read-only — affiche la note, l'appréciation courte et les 3 principaux
// leviers. Cliquer "Voir détail" ouvre le dialog complet.
export default function ChantierQualificationSynthesisPanel({ prospect, currentUser }) {
    const [summary, setSummary] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        api.get(`/prospects/${prospect.id}/chantier-energy-summary`)
            .then((r) => setSummary(r.data))
            .catch(() => setSummary({ status: 'none' }));
    }, [prospect.id]);

    if (!summary || summary.status !== 'validated') return null;
    const q = summary.qualification;

    const noteTone = (n) => n >= 4 ? 'bg-emerald-600 text-white'
        : n >= 3 ? 'bg-blue-600 text-white'
        : n >= 2 ? 'bg-amber-500 text-white'
        : 'bg-slate-500 text-white';

    const topLeviers = (q.leviers || []).slice(0, 3);

    return (
        <>
            <Card className="border-l-4 border-l-blue-500 bg-blue-50/30" data-testid="chantier-qualif-synthesis-panel">
                <CardContent className="py-3">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <ClipboardList className="w-4 h-4 text-blue-700" />
                            <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                                Qualification énergétique
                            </span>
                            <Badge className={noteTone(q.display_note)} data-testid="synthesis-note">
                                {q.display_note}/4
                            </Badge>
                            {q.is_provisional && (
                                <Badge variant="outline" className="text-[10px]">Provisoire</Badge>
                            )}
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <p className="text-sm text-slate-700 line-clamp-2">{q.appreciation}</p>
                            {topLeviers.length > 0 && (
                                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    <TrendingUp className="w-3 h-3 text-emerald-600" />
                                    {topLeviers.map((l, i) => (
                                        <span key={i}
                                              className="text-[11px] px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-700">
                                            {l.length > 45 ? l.slice(0, 42) + '…' : l}
                                        </span>
                                    ))}
                                    {q.leviers.length > 3 && (
                                        <span className="text-[11px] text-slate-500">
                                            + {q.leviers.length - 3} autre{q.leviers.length - 3 > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <Button onClick={() => setDetailOpen(true)} size="sm" variant="outline"
                                data-testid="synthesis-view-detail-btn">
                            <Eye className="w-3.5 h-3.5 mr-1.5" /> Voir détail
                        </Button>
                    </div>
                    <p className="text-[11px] text-slate-400 italic mt-2">
                        Estimation issue d&apos;une pré-étude. La rentabilité définitive du dossier est confirmée par l&apos;étude technique du bureau d&apos;études.
                    </p>
                </CardContent>
            </Card>
            <ChantierQualificationDetailDialog
                open={detailOpen} onOpenChange={setDetailOpen}
                qualification={q} currentUser={currentUser} canEdit={false}
            />
        </>
    );
}
